// RewardCard.js
import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { redeemReward, incrementRedeemCount, createRedemption } from '../api/rewards';
import { addRedeemedReward } from '../api/users';
import RedemptionQRModal from './RedemptionQRModal';
import './RewardCard.css';

const RewardCard = ({ reward, userTier, isRedeemed, onRedeemSuccess }) => {
  const { user, userData, refreshUserData } = useContext(AuthContext);
  const [isRedeeming, setIsRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);
  const [redemptionId, setRedemptionId] = useState(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const canRedeem = userTier >= reward.requiredTier && !isRedeemed && reward.active;

  // Check if description is long enough to need "Read More"
  const DESCRIPTION_LIMIT = 100;
  const isDescriptionLong = reward.description && reward.description.length > DESCRIPTION_LIMIT;
  const displayDescription = isDescriptionLong && !isDescriptionExpanded
    ? reward.description.substring(0, DESCRIPTION_LIMIT) + '...'
    : reward.description;

  const handleShowQR = async () => {
    setIsRedeeming(true);
    try {
      // Create a new redemption record (allows unlimited redemptions)
      const newRedemptionId = await createRedemption(user.uid, reward.id, userData, reward);
      console.log('New redemption created with ID:', newRedemptionId);

      // Show QR modal
      setRedemptionId(newRedemptionId);
      setShowQRModal(true);
    } catch (error) {
      console.error('Error creating redemption:', error);
      setRedeemError(error.message || 'Failed to generate QR code');
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleRedeem = async () => {
    if (!canRedeem) return;

    if (!user || !user.uid) {
      setRedeemError('User not authenticated');
      return;
    }

    if (!window.confirm(`Redeem this offer?`)) {
      return;
    }

    setIsRedeeming(true);
    setRedeemError(null);

    try {
      console.log('Starting redemption process...');

      // Attempt to redeem the reward
      await redeemReward(user.uid, reward.id, userTier);
      console.log('Reward redeemed successfully');

      // Create redemption record with QR code
      const newRedemptionId = await createRedemption(user.uid, reward.id, userData, reward);
      console.log('Redemption created with ID:', newRedemptionId);

      // Add to user's redeemed rewards
      await addRedeemedReward(user.uid, reward.id);

      // Increment redeem count for the reward
      await incrementRedeemCount(reward.id);

      // Set redemption ID and show QR modal
      setRedemptionId(newRedemptionId);
      setShowQRModal(true);
      console.log('QR Modal should now be visible');

    } catch (error) {
      console.error('Error redeeming reward:', error);
      setRedeemError(error.message || 'Failed to redeem reward');
    } finally {
      setIsRedeeming(false);
    }
  };

  return (
    <div className={`reward-card ${isRedeemed ? 'redeemed' : ''} ${!canRedeem && !isRedeemed ? 'locked' : ''}`}>
      {/* Merchant Logo/Image */}
      {reward.imageUrl ? (
        <div className="reward-image">
          <img src={reward.imageUrl} alt={reward.merchantName} />
        </div>
      ) : (
        <div className="reward-image placeholder">
          <div className="merchant-initial">
            {reward.merchantName?.charAt(0).toUpperCase() || 'M'}
          </div>
        </div>
      )}

      {/* Status Badge */}
      {isRedeemed && (
        <div className="reward-badge redeemed-badge">Redeemed</div>
      )}
      {!reward.active && (
        <div className="reward-badge inactive-badge">Inactive</div>
      )}

      {/* Card Content */}
      <div className="reward-content">
        <div className="merchant-info">
          <h3 className="merchant-name">{reward.merchantName}</h3>
          {reward.category && (
            <span className="reward-category">{reward.category}</span>
          )}
        </div>

        <h4 className="reward-title">{reward.title}</h4>

        <div className="reward-description-container">
          <p className="reward-description">
            {displayDescription}
          </p>
          {isDescriptionLong && (
            <button
              className="read-more-btn"
              onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
            >
              {isDescriptionExpanded ? 'Read Less' : 'Read More'}
            </button>
          )}
        </div>

        {/* Discount Display */}
        <div className="discount-display">
          <span className="discount-value">{reward.discount}</span>
          <span className="discount-label">OFF</span>
        </div>

        {/* Tier Requirement */}
        <div className="tier-requirement">
          <span className="tier-icon">⭐</span>
          <span className="tier-text">Tier {reward.requiredTier} Required</span>
        </div>

        {/* Terms & Conditions */}
        {reward.terms && (
          <div className="reward-terms">
            <p>{reward.terms}</p>
            {reward.validUntil && (
              <p className="validity">Valid until: {new Date(reward.validUntil).toLocaleDateString()}</p>
            )}
          </div>
        )}

        {/* Error Message */}
        {redeemError && (
          <div className="redeem-error">
            {redeemError}
          </div>
        )}

        {/* Action Button */}
        {!isRedeemed && (
          <button
            className={`redeem-btn ${canRedeem ? 'can-redeem' : 'locked'}`}
            onClick={handleRedeem}
            disabled={!canRedeem || isRedeeming}
          >
            {isRedeeming ? 'Redeeming...' :
              canRedeem ? 'Redeem Now' :
                `Requires Tier ${reward.requiredTier}`}
          </button>
        )}

        {isRedeemed && (
          <button
            className="redeem-btn can-redeem"
            onClick={handleShowQR}
            disabled={isRedeeming}
          >
            Show QR Code
          </button>
        )}
      </div>

      {/* QR Code Modal */}
      <RedemptionQRModal
        isOpen={showQRModal}
        onClose={() => {
          setShowQRModal(false);
          // Refresh data when modal is closed
          if (refreshUserData) {
            refreshUserData();
          }
          if (onRedeemSuccess) {
            onRedeemSuccess();
          }
        }}
        redemptionId={redemptionId}
        reward={reward}
      />
    </div>
  );
};

export default RewardCard;
